import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { Icon } from '@iconify/react';
import RichTextEditor from 'src/components/form/fields/RichTextEditor';
import HtmlTemplateUploadSection from './HtmlTemplateUploadSection';
import { useGetMembersQuery } from 'src/features/members/membersApiSlice';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const computeFormalGreeting = (member) => {
  const title = String(member?.title || '').trim().toLowerCase();
  const gender = String(member?.gender || '').trim().toLowerCase();
  const lastName = member?.lastName || '';
  const firstName = member?.firstName || '';
  const fullName = `${firstName} ${lastName}`.trim();

  if (
    title === 'herr' ||
    gender === 'm' ||
    gender === 'male' ||
    gender === 'männlich'
  ) {
    return `Sehr geehrter Herr ${lastName}`;
  }
  if (
    title === 'frau' ||
    gender === 'f' ||
    gender === 'female' ||
    gender === 'weiblich'
  ) {
    return `Sehr geehrte Frau ${lastName}`;
  }
  return `Guten Tag ${fullName}`;
};

const renderTemplatePreview = (template, member, tenantName) => {
  if (!template) return '';
  if (!member) return template;

  const today = new Date().toLocaleDateString('de-DE');
  const firstName = member?.firstName || '';
  const lastName = member?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const contractEmail =
    member?.contractRef?.email || member?.mail || '[E-Mail-Adresse]';
  const contractOwner =
    (member?.contractRef?.firstName
      ? `${member.contractRef.firstName} ${member.contractRef.lastName || ''}`.trim()
      : null) || fullName || '[Vertragsinhaber]';

  return (
    template
      // Mustache blocks: assume hasReason = true for preview
      .replace(/\{\{#hasReason\}\}([\s\S]*?)\{\{\/hasReason\}\}/g, '$1')
      .replace(/\{\{\^hasReason\}\}([\s\S]*?)\{\{\/hasReason\}\}/g, '')
      // Mustache blocks: generic positive/negative
      .replace(/\{\{#activationUrl\}\}([\s\S]*?)\{\{\/activationUrl\}\}/g, '$1')
      // Token replacements
      .replace(/\{\{formalGreeting\}\}/g, computeFormalGreeting(member))
      .replace(/\{\{memberFirstName\}\}/g, firstName)
      .replace(/\{\{memberLastName\}\}/g, lastName)
      .replace(/\{\{memberFullName\}\}/g, fullName)
      .replace(/\{\{contractOwner\}\}/g, contractOwner)
      .replace(/\{\{contractEmail\}\}/g, contractEmail)
      .replace(
        /\{\{activationUrl\}\}/g,
        "<em style=\"color:#666\">[Aktivierungslink \u2013 wird beim echten Versand generiert]</em>"
      )
      .replace(/\{\{tenantName\}\}/g, tenantName || '[Vereinsname]')
      .replace(/\{\{today\}\}/g, today)
      // Termination-specific tokens
      .replace(/\{\{terminationDate\}\}/g, today)
      .replace(/\{\{lastMonth\}\}/g, '[Letzter Beitragsmonat]')
      .replace(/\{\{contractEndDate\}\}/g, '[Vertragsende-Datum]')
      .replace(/\{\{noticePeriod\}\}/g, '[3]')
      .replace(/\{\{contractDate\}\}/g, '[Vertragsbeginn-Datum]')
      .replace(/\{\{contractDuration\}\}/g, '[12]')
      .replace(/\{\{contractExtension\}\}/g, '[12]')
      // Rejection-specific
      .replace(
        /\{\{reason\}\}/g,
        "<em style=\"color:#666\">[Ablehnungsgrund \u2013 wird vom Admin angegeben]</em>"
      )
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const MailTemplatePageLayout = ({
  title,
  description,
  backPath = '/dashboard/settings/mail',
  subjectFieldName,
  templateFieldName,
  bccFieldName,
  templateType,
  modeFieldName,
  currentHtmlTemplate,
  tokens,
  additionalInfo,
  control,
  register,
  handleInsertToken,
  editorRef,
  handleSubmit,
  onSubmit,
  handleSendTest,
  isSaving,
  isTesting,
  tenantName,
  navigate,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedPreviewMember, setSelectedPreviewMember] = useState(null);

  const { data: members } = useGetMembersQuery();
  const memberList = useMemo(
    () => (Array.isArray(members) ? members : []),
    [members]
  );

  // Watch template text for used-tokens display and preview
  const templateValue = useWatch({ control, name: templateFieldName }) || '';

  const usedTokens = useMemo(
    () =>
      Array.from(
        new Set(templateValue.match(/\{\{\s*[\w.#/^]+\s*\}\}/g) || [])
      ),
    [templateValue]
  );

  const previewHtml = useMemo(
    () => renderTemplatePreview(templateValue, selectedPreviewMember, tenantName),
    [templateValue, selectedPreviewMember, tenantName]
  );

  const subjectValue = useWatch({ control, name: subjectFieldName }) || '';
  const modeWatchValue = useWatch({
    control,
    name: modeFieldName || '__templateModeFallback__',
  });
  const modeValue = modeWatchValue || 'auto';
  const selectedMode = ['auto', 'text', 'html'].includes(modeValue)
    ? modeValue
    : 'auto';
  const hasHtmlTemplate = Boolean((currentHtmlTemplate || '').trim());

  const modeStatus = useMemo(() => {
    if (selectedMode === 'text') {
      return {
        severity: 'info',
        text: 'Versand erfolgt immer mit der Text-Vorlage.',
      };
    }
    if (selectedMode === 'html') {
      if (hasHtmlTemplate) {
        return {
          severity: 'success',
          text: 'Versand erfolgt immer mit der HTML-Vorlage.',
        };
      }
      return {
        severity: 'warning',
        text: 'HTML ist gewählt, aber keine HTML-Vorlage hinterlegt. Beim Versand wird automatisch die Text-Vorlage genutzt.',
      };
    }
    if (hasHtmlTemplate) {
      return {
        severity: 'success',
        text: 'Automatisch aktiv: HTML-Vorlage wird verwendet, Text bleibt als Fallback erhalten.',
      };
    }
    return {
      severity: 'info',
      text: 'Automatisch aktiv: Es ist keine HTML-Vorlage hinterlegt, daher wird die Text-Vorlage verwendet.',
    };
  }, [hasHtmlTemplate, selectedMode]);

  return (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          variant='contained'
          color='secondary'
          size='large'
          onClick={() => navigate(backPath)}
        >
          Zurück
        </Button>
        <Typography variant='h3'>{title}</Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {additionalInfo && <Box sx={{ mb: 3 }}>{additionalInfo}</Box>}

      {description && (
        <Typography variant='body1' sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}

      {modeFieldName && (
        <Controller
          name={modeFieldName}
          control={control}
          defaultValue='auto'
          render={({ field }) => (
            <Box
              sx={{
                mb: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                background:
                  'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(56,142,60,0.06) 100%)',
              }}
            >
              <Typography variant='subtitle1' sx={{ mb: 0.5, fontWeight: 700 }}>
                Versandmodus
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Wählen Sie, welche Vorlage der Server für diese E-Mail-Art beim
                Versand priorisiert.
              </Typography>
              <ToggleButtonGroup
                value={selectedMode}
                exclusive
                onChange={(_, nextValue) => {
                  if (!nextValue) return;
                  field.onChange(nextValue);
                }}
                size='small'
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                <ToggleButton value='auto'>Automatisch</ToggleButton>
                <ToggleButton value='text'>Immer Text</ToggleButton>
                <ToggleButton value='html' disabled={!hasHtmlTemplate}>
                  Immer HTML
                </ToggleButton>
              </ToggleButtonGroup>
              <Alert severity={modeStatus.severity} sx={{ mt: 2, mb: 0 }}>
                {modeStatus.text}
              </Alert>
            </Box>
          )}
        />
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label='Text-Vorlage' />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              HTML-Vorlage
              {currentHtmlTemplate?.trim() && (
                <Chip
                  label='Aktiv'
                  color='success'
                  size='small'
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              )}
            </Box>
          }
        />
      </Tabs>

      {/* ── Tab 0: Text-Vorlage ── */}
      {activeTab === 0 && (
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Subject + BCC */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label='Betreff'
                fullWidth
                {...register(subjectFieldName)}
                disabled={isSaving || isTesting}
              />
            </Grid>
            {bccFieldName && (
              <Grid item xs={12} md={6}>
                <TextField
                  label='BCC Empfänger'
                  fullWidth
                  {...register(bccFieldName)}
                  disabled={isSaving || isTesting}
                  helperText='Optional: E-Mail-Adresse für eine Blindkopie (BCC).'
                />
              </Grid>
            )}
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Token section */}
          <Typography variant='h6' sx={{ mb: 1 }}>
            Verfügbare Platzhalter (Tokens)
          </Typography>
          <Alert severity='info' sx={{ mb: 2 }}>
            <strong>So verwenden Sie Platzhalter:</strong> Klicken Sie auf einen
            Platzhalter, um ihn an der aktuellen Cursorposition in den Text
            einzufügen. Beim E-Mail-Versand wird er automatisch durch den
            jeweiligen Wert ersetzt – z.&thinsp;B. wird{' '}
            <code>{'{{formalGreeting}}'}</code> je nach hinterlegtem Titel
            durch <em>'Sehr geehrter Herr Mustermann'</em> oder{' '}
            <em>'Sehr geehrte Frau Musterfrau'</em> ersetzt (ohne Titel:{' '}
            <em>'Guten Tag Max Mustermann'</em>).
          </Alert>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {tokens.map((token) => (
              <Tooltip
                key={token.value}
                title={
                  <Box>
                    <Typography variant='caption' display='block' sx={{ fontFamily: 'monospace', mb: 0.5 }}>
                      {token.value}
                    </Typography>
                    <Typography variant='caption'>{token.desc}</Typography>
                  </Box>
                }
                arrow
                placement='top'
              >
                <Chip
                  label={token.label}
                  onClick={() => handleInsertToken(token.value)}
                  variant='outlined'
                  size='small'
                  clickable
                  icon={<Icon icon='mdi:cursor-text' width={14} />}
                />
              </Tooltip>
            ))}
          </Box>

          {/* Rich text editor */}
          <Controller
            name={templateFieldName}
            control={control}
            render={({ field }) => (
              <RichTextEditor
                value={field.value}
                onChange={field.onChange}
                disabled={isSaving || isTesting}
                editorRef={editorRef}
              />
            )}
          />

          {/* Used tokens */}
          {usedTokens.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                Im Text verwendete Platzhalter:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {usedTokens.map((t) => (
                  <Chip key={t} label={t} size='small' />
                ))}
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Test mail + preview + save */}
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={6}>
              <TextField
                label='Test-E-Mail-Adresse'
                fullWidth
                {...register('testRecipient')}
                disabled={isSaving || isTesting}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant='outlined'
                  onClick={handleSendTest}
                  disabled={isSaving || isTesting}
                  startIcon={<Icon icon='mdi:email-send-outline' />}
                >
                  Test-E-Mail senden
                </Button>
                <Button
                  variant='outlined'
                  color='info'
                  onClick={() => setPreviewOpen(true)}
                  startIcon={<Icon icon='mdi:eye-outline' />}
                >
                  Vorschau
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Box display='flex' justifyContent='flex-end' sx={{ mt: 3 }}>
            <Button
              variant='contained'
              color='secondary'
              type='submit'
              disabled={isSaving || isTesting}
            >
              Einstellungen speichern
            </Button>
          </Box>
        </form>
      )}

      {/* ── Tab 1: HTML-Vorlage ── */}
      {activeTab === 1 && (
        <HtmlTemplateUploadSection
          templateType={templateType}
          currentHtmlTemplate={currentHtmlTemplate}
          members={memberList}
          tenantName={tenantName}
        />
      )}

      {/* ── Preview dialog ── */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>E-Mail Vorschau — Text-Vorlage</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Wählen Sie ein Mitglied aus, um die E-Mail mit echten Daten
            zu simulieren. Platzhalter wie{' '}
            <code>{'{{activationUrl}}'}</code> oder{' '}
            <code>{'{{reason}}'}</code> werden mit Beispielwerten dargestellt.
          </Typography>

          <Autocomplete
            options={memberList}
            getOptionLabel={(m) =>
              `${m?.firstName || ''} ${m?.lastName || ''}`.trim() || '—'
            }
            value={selectedPreviewMember}
            onChange={(_, v) => setSelectedPreviewMember(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Mitglied für Vorschau auswählen'
                size='small'
                sx={{ mb: 3 }}
              />
            )}
          />

          {selectedPreviewMember ? (
            <>
              {/* Subject preview */}
              {subjectValue && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant='caption' color='text.secondary'>
                    Betreff:
                  </Typography>
                  <Typography variant='body2' fontWeight='bold'>
                    {subjectValue}
                  </Typography>
                </Box>
              )}
              {/* Body preview */}
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  minHeight: 150,
                  fontFamily: 'sans-serif',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                  bgcolor: 'background.paper',
                }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </>
          ) : (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Icon icon='mdi:account-search-outline' width={40} style={{ opacity: 0.4 }} />
              <Typography color='text.secondary' sx={{ mt: 1 }}>
                Bitte wählen Sie ein Mitglied aus, um die Vorschau anzuzeigen.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MailTemplatePageLayout;
